import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { getCandidates } from "../services/candidateApi";
import { deleteUnlinkedTestResult, getUnlinkedTestResults, linkTestResult } from "../services/testResultLinksApi";

function displayDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
    return match ? `${match[3]}.${match[2]}.${match[1]}` : value || "—";
}

function scoreText(item) {
    if (item.max_score) return `${item.score ?? 0} из ${item.max_score}`;
    return null;
}

function CandidateSearch({ initial, onPick, testId }) {
    const [query, setQuery] = useState(initial || "");
    const [found, setFound] = useState([]);
    const [busy, setBusy] = useState(false);

    const search = async () => {
        const text = query.trim();
        if (text.length < 2) return;
        setBusy(true);
        try {
            const res = await getCandidates(null, text, null, null, undefined, 0, 10, null, { includeArchived: true });
            setFound(Array.isArray(res) ? res : res?.items || []);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap gap-2">
                <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => { if (event.key === "Enter") search(); }}
                    placeholder="ФИО кандидата"
                    data-testid={`unlinked-search-${testId}`}
                    className="min-w-[220px] flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                />
                <button type="button" onClick={search} disabled={busy} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100 disabled:opacity-50">
                    {busy ? "Ищем…" : "Найти"}
                </button>
            </div>
            {found.length ? (
                <ul className="mt-2 space-y-1">
                    {found.map((candidate) => (
                        <li key={candidate.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm">
                            <span>
                                <b>{candidate.full_name}</b>
                                <span className="text-slate-500"> · {candidate.last_vacancy_title || "без вакансии"} · {candidate.current_status || candidate.stage || ""}</span>
                            </span>
                            <button type="button" onClick={() => onPick(candidate.id)} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">
                                Привязать сюда
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

function ResultCard({ item, onLinked }) {
    const { showAlert } = useAlertContext();
    const [saving, setSaving] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const key = `${item.kind}-${item.id}`;

    const link = async (candidateId) => {
        setSaving(true);
        try {
            await linkTestResult(item.kind, item.id, candidateId);
            showAlert("Результат привязан к карточке кандидата", "success");
            onLinked(key);
        } catch (error) {
            showAlert(error?.message || "Не удалось привязать результат", "error");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Удалить результат «${item.test_name || "тест"}» (${item.full_name})? Восстановить будет нельзя.`)) return;
        setSaving(true);
        try {
            await deleteUnlinkedTestResult(item.kind, item.id);
            showAlert("Результат удалён", "success");
            onLinked(key);
        } catch (error) {
            showAlert(error?.message || "Не удалось удалить результат", "error");
        } finally {
            setSaving(false);
        }
    };

    const score = scoreText(item);
    return (
        <article className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm" data-testid={`unlinked-${key}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-lg font-bold text-slate-900">{item.full_name}</p>
                    <p className="mt-1 text-sm text-slate-500">
                        {item.test_name} · {item.position || "должность не указана"} · пройден {displayDate(item.taken_at)}
                        {item.birth_date ? ` · д.р. ${displayDate(item.birth_date)}` : ""}
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {score ? <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-800">{score}</div> : null}
                    <button
                        type="button"
                        disabled={saving}
                        onClick={remove}
                        data-testid={`unlinked-delete-${key}`}
                        className="rounded-xl border border-rose-200 px-3 py-1 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                        Удалить
                    </button>
                </div>
            </div>

            {item.suggestions?.length ? (
                <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {item.suggestions.length === 1 ? "Похоже, это кандидат" : "Несколько похожих карточек — выберите нужную"}
                    </p>
                    {item.suggestions.map((candidate) => (
                        <div key={candidate.candidate_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                            <span className="text-sm">
                                <Link to={`/candidates?candidate_id=${candidate.candidate_id}`} className="font-semibold text-slate-900 hover:underline">
                                    {candidate.full_name}
                                </Link>
                                {candidate.birth_date ? <span className="text-slate-500"> · д.р. {displayDate(candidate.birth_date)}</span> : null}
                                {candidate.hh_resume_link ? (
                                    <a href={candidate.hh_resume_link} target="_blank" rel="noreferrer" className="ml-2 text-[#9b7c13] hover:underline">резюме HH</a>
                                ) : null}
                            </span>
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => link(candidate.candidate_id)}
                                className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                            >
                                Привязать
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                    Карточка не найдена. Найдите кандидата вручную или сначала заведите его карточку (например, импортом отклика с HH).
                </p>
            )}

            <button type="button" onClick={() => setSearchOpen((open) => !open)} className="mt-3 text-sm font-semibold text-[#9b7c13] hover:underline">
                {searchOpen ? "Скрыть поиск" : "Найти другого кандидата"}
            </button>
            {searchOpen ? <CandidateSearch initial={item.full_name} onPick={link} testId={key} /> : null}
        </article>
    );
}

export default function UnlinkedTestResultsPage() {
    const { showAlert } = useAlertContext();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await getUnlinkedTestResults();
            setItems(Array.isArray(rows) ? rows : []);
        } catch (error) {
            showAlert(error?.message || "Не удалось загрузить результаты", "error");
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => { load(); }, [load]);

    const onLinked = (key) => setItems((prev) => prev.filter((item) => `${item.kind}-${item.id}` !== key));

    return (
        <MainLayout>
            <div className="mx-auto max-w-5xl space-y-4 p-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Непривязанные результаты тестов</h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Тесты, пройденные по общей ссылке, без карточки кандидата. Если похожая карточка ровно одна,
                        система привязывает результат сама в течение нескольких минут. Здесь остаются спорные случаи
                        и те, у кого карточки ещё нет.
                    </p>
                </div>
                {loading ? <p className="text-sm text-slate-500">Загрузка…</p> : null}
                {!loading && !items.length ? (
                    <p className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">Все результаты привязаны к карточкам.</p>
                ) : null}
                {items.map((item) => (
                    <ResultCard key={`${item.kind}-${item.id}`} item={item} onLinked={onLinked} />
                ))}
            </div>
        </MainLayout>
    );
}
